import type {LinkField, LinkToMediaField} from '@prismicio/client'
import {isFilled} from '@prismicio/client'
import type {ButtonDocument} from 'prismicio-types'
import {GET_MOSS_HOSTNAME, locales} from 'src/common/constants'

const getLocaleFromWebUrl = (url: string): string | undefined => {
  const path = url.startsWith(GET_MOSS_HOSTNAME) ? url.slice(GET_MOSS_HOSTNAME.length) : url
  return Object.values(locales).find((locale) => new RegExp(`^/${locale}(?:[/?#]|$)`, 'i').test(path))
}

/**
 * Returns the locale that matches the href the link will resolve to, so next/link does not
 * prefetch a path with a mismatched locale prefix.
 * Document links use their own lang, web links on our host (or relative) use the locale
 * already present in their path, anything else uses the current locale.
 *
 * @param link - The Prismic LinkField to extract locale from
 * @param currentLocale - The locale of the current page (router.locale)
 * @returns The locale string, or undefined when no locale can be determined
 *
 * @example
 * const {locale} = useRouter()
 * <Link href={linkResolver(link)} locale={getLinkLocale(link, locale)}>Click me</Link>
 */
export function getLinkLocale(
  link: LinkField | undefined | null,
  currentLocale: string | undefined,
): string | undefined {
  if (!isFilled.link(link)) {
    return currentLocale
  }

  if ('lang' in link) {
    return link.lang
  }

  if (link.link_type === 'Web') {
    return getLocaleFromWebUrl(link.url) ?? currentLocale
  }

  return currentLocale
}

/**
 * Safely extracts the URL from a Prismic LinkToMediaField.
 * Returns the URL if the media link is filled, otherwise returns the fallback.
 *
 * @param link - The Prismic LinkToMediaField to extract URL from
 * @param fallback - The fallback URL to use if link is not filled (defaults to empty string)
 * @returns The URL string
 *
 * @example
 * const videoUrl = getMediaLinkUrl(slice.primary.video)
 * <VideoPlayer src={videoUrl} />
 */
export function getMediaLinkUrl(link: LinkToMediaField | undefined | null, fallback: string = ''): string {
  return isFilled.linkToMedia(link) ? link.url : fallback
}

/**
 * Safely extracts the URL from a Prismic LinkToMediaField.
 * Returns the URL if the media link is filled, otherwise returns the fallback.
 *
 * @param link - The Prismic LinkToMediaField to extract URL from
 * @param fallback - The fallback URL to use if link is not filled (defaults to empty string)
 * @returns The URL string
 *
 * @example
 * const videoUrl = getMediaLinkUrl(slice.primary.video)
 * <VideoPlayer src={videoUrl} />
 */
export function getLinkUrl(link: LinkField | undefined | null, fallback: string = ''): string {
  return isFilled.link(link) && link.url ? link.url : fallback
}

/**
 * Safely extracts the target from a Prismic LinkField.
 * Returns the target if the link is filled and has a target property, otherwise returns the fallback.
 *
 * @param link - The Prismic LinkField to extract target from
 * @param fallback - The fallback target to use if link is not filled or has no target property (defaults to '_self')
 * @returns The target string ('_self', '_blank', etc.)
 *
 * @example
 * const target = getLinkTarget(tile.link_url)
 * <Link href={url} target={target}>Click me</Link>
 */
export function getLinkTarget(link: LinkField | undefined | null, fallback: string = '_self'): string {
  return isFilled.link(link) && 'target' in link ? (link.target ?? fallback) : fallback
}

/**
 * Resolves a generic Link field that may point either directly to a URL/document, or to a `button`
 * document acting as an indirection layer. Buttons carry their own `link`, so when the given link
 * targets one, its inner link is returned instead.
 *
 * @example
 * const resolvedLink = getResolvedLink({link: slice.primary.submit_link, buttonsData})
 * const href = isFilled.link(resolvedLink) ? linkResolver(resolvedLink) : undefined
 */
export const getResolvedLink = ({
  link,
  buttonsData,
}: {
  link?: LinkField
  buttonsData?: ButtonDocument[]
}): LinkField | undefined => {
  const linkedButtonId =
    isFilled.link(link) && link.link_type === 'Document' && link.type === 'button' ? link.id : undefined

  const linkedButtonData = linkedButtonId
    ? buttonsData?.find((button) => button.id === linkedButtonId)?.data
    : undefined

  return linkedButtonData?.link ?? link
}
